import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "contexts/i18nContext";
import React, { ChangeEvent, useState } from "react";
import {
  addAccusedSummonFeedback,
  editAccusedSummon,
} from "services/prosecutorDecipilineService";
import { AccusedSummon, Complaints } from "types";
import { LoadingIcon } from "utils/constant";
import {
  ACCUSED_COMPLAINT_STATUS,
  APPEAL_FEEDBACK_TYPE,
} from "utils/constantValues";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: AccusedSummon;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddSummonFeedbackModal({
  isOpen,
  setIsOpen,
  complaintData,
  summonData,
  onRefresh,
}: ModalProps) {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    feedback_status: APPEAL_FEEDBACK_TYPE?.APPROVED,
    feedback: "",
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!summonData) {
      showToast(t("summonNotFound"), "error");
      return;
    }
    setLoading(true);

    const isAccusedConfirmed = true;
    const isCoordinatorConfirmed = true;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("cordinator_confirmed", isCoordinatorConfirmed.toString());
    data.append("feedback_status", formState?.feedback_status);
    data.append(
      "status",
      formState.feedback_status === APPEAL_FEEDBACK_TYPE.APPROVED
        ? ACCUSED_COMPLAINT_STATUS.APPROVED
        : ACCUSED_COMPLAINT_STATUS.REVISION,
    );
    data.append(
      "accused_prosecutor_name",
      `${complaintData?.accused_prosecutor_name || ""}`,
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    data.append(
      "assigned_prosecutor",
      complaintData?.assigned_prosecutor || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      const resData = await editAccusedSummon(data, summon_id, isFormData);

      if (resData) {
        await addAccusedSummonFeedback({
          appeal_request: summonData?.id,
          comment: formState?.feedback,
        });
      }

      onRefresh();
      showToast(t("successfullyConfirmed"), "success");
      setIsOpen(false);
      setFormState({
        feedback_status: APPEAL_FEEDBACK_TYPE?.APPROVED,
        feedback: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("addFeedback")}>
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("feedbackType")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="feedback_status"
              value={formState.feedback_status}
              onChange={handleChange}
            >
              {Object.values(APPEAL_FEEDBACK_TYPE)?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("feedback")}
            </label>
            <textarea
              required
              rows={7}
              name="feedback"
              value={formState.feedback}
              onChange={handleChange}
              placeholder={t("detailsPlaceholder")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addFeedback")
          )}
        </button>
      </form>
    </SideModal>
  );
}
